/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import { vappContainer } from '@/application';
import { BreadcrumbService } from '@/services/breadcrumbService';
import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { toHeaderCase } from 'js-convert-case';
import { editProfile } from '../../config/editProfile.config';
import { error } from '../../config/error.config';
import { featuresSection } from '../../config/featuresSection.config';
import { formData } from '../../config/formData.config';
import { header } from '../../config/header.config';
import { loading } from '../../config/loading.config';
import { notifications } from '../../config/notifications.config';
export interface IStaticPageContentMethods extends IPluginMethodMap {
  getStaticPageContentSync(args?: any): any;
}

const getBreadcrumbService = () => {
  return vappContainer.get<BreadcrumbService>(BreadcrumbService.displayName);
};
export class StaticPageContentService implements IAgentPlugin {
  readonly methods: IStaticPageContentMethods;

  constructor() {
    this.methods = {
      getStaticPageContentSync: this.getProps.bind(this),
    };
  }

  private getOrgListItems(res: any) {
    let _temp = [];
    _temp = featuresSection?.orgListItems?.map((item) => {
      if (item?.title === 'Company Registration Number') {
        return {
          ...item,
          subTitle: res?.cNumber ? `${res?.cNumber}` : '-',
        };
      }

      if (item?.title === 'Address') {
        return {
          ...item,
          subTitle: res?.address ? `${res?.address}` : '-',
        };
      }
      if (item?.title === 'City/Town') {
        return {
          ...item,
          subTitle: res?.city ?? '-',
        };
      }

      if (item?.title === 'Country') {
        return {
          ...item,
          subTitle: '-',
          flag: res?.country,
        };
      }
      if (item?.title === 'Email') {
        return {
          ...item,
          subTitle: res?.email ?? '-',
        };
      }
      if (item?.title === 'Phone Number') {
        return {
          ...item,
          subTitle: res?.telephone ?? '-',
        };
      }
      if (item?.title === 'Website') {
        return {
          ...item,
          subTitle: res?.website ?? '-',
        };
      }
    });
    return _temp;
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = featuresSection?.listItems?.map((item) => {
      if (item?.title === 'Last Name') {
        return {
          ...item,
          subTitle: res?.lastName ?? '-',
        };
      }
      if (item?.title === 'First Name') {
        return {
          ...item,
          subTitle: res?.firstName ?? '-',
        };
      }

      if (item?.title === 'Middle Name') {
        return {
          ...item,
          subTitle: res?.middleName ?? '-',
        };
      }

      if (item?.title === 'Role') {
        return {
          ...item,
          subTitle: res?.role ?? '-',
        };
      }
    });
    return _temp;
  }

  private getDirectorData = (res: any) => {
    let _temp = [];
    _temp = res?.map((item: any, key: number) => {
      return {
        title: `Director ${key + 1}`,
        subTitle: `${toHeaderCase(
          item?.directorFirstName ?? ''
        )} ${toHeaderCase(item?.directorMiddleName ?? '')} ${toHeaderCase(
          item?.directorLastName ?? ''
        )}`,
      };
    });
    return _temp;
  };
  private getCompanyData = (res: any) => {
    let _temp = [];
    _temp = featuresSection?.companyListItems?.map((item) => {
      if (item?.title === 'Street 1') {
        return {
          ...item,
          subTitle: res?.address1 ? `${res?.address1}` : '-',
        };
      }
      if (item?.title === 'Street 2') {
        return {
          ...item,
          subTitle: res?.address2 ? `${res?.address2}` : '-',
        };
      }
      if (item?.title === 'Incorporation Date') {
        return {
          ...item,
          subTitle: res?.incorporationDate ?? '-',
        };
      }
      if (item?.title === 'Nature of Business') {
        return {
          ...item,
          subTitle: res?.natureOfBusiness ? `${res?.natureOfBusiness}` : '-',
        };
      }
      if (item?.title === 'Parish') {
        return {
          ...item,
          subTitle: res?.parish ?? '-',
        };
      }
      if (item?.title === 'Website') {
        return {
          ...item,
          subTitle: res?.website ?? '-',
        };
      }
      if (item?.title === 'Taxpayer Registration No') {
        return {
          ...item,
          subTitle: res?.taxpayerRegNo ?? '-',
        };
      }
      if (item?.title === 'Company Type') {
        return {
          ...item,
          subTitle: res?.companyType ?? '-',
        };
      }
      if (item?.title === 'Postal Code') {
        return {
          ...item,
          subTitle: res?.postalCode ?? '-',
        };
      }
      if (item?.title === 'Street Address') {
        return {
          ...item,
          subTitle: res?.address ? `${res?.address}` : '-',
        };
      }
      if (item?.title === 'City/Town') {
        return {
          ...item,
          subTitle: res?.city ?? '-',
        };
      }
    });
    return _temp;
  };

  private format = (_res: any) => {
    return {
      title: `${toHeaderCase(_res?.lastName ?? '')} ${toHeaderCase(
        _res?.firstName ?? ''
      )}`,
      subtitle: _res?.vuid ? `Account ID: ${_res?.vuid}` : _res?.occupation,
      heroImage: {
        image:
          _res?.image ??
          `https://ui-avatars.com/api/?name=${_res.lastName}+${_res?.firstName}&background=0D8ABC&color=fff`,
      },
      listItems: this.getListItems(_res),
      initialData: {
        lastName: _res?.lastName,
        firstName: _res?.firstName,
        image: _res?.image,
        telephone: _res?.primaryContactNo,
        address: _res?.address?.addressLine1,
        city: _res?.address?.city,
        country: _res?.address?.country,
        occupation: _res?.occupation,
      },
      // statusComp: {
      //   title: `Review Pending`,
      //   statusType: 'warning',
      // },
      // buttons: featuresSection?.buttons,
      // accordionItems: [
      //   {
      //     type: 'multi-col-list',
      //     numberOfColumns: '3',
      //     title: 'Company Details',
      //     data: this.getCompanyData(_res),
      //   },
      //   {
      //     type: 'multi-col-list',
      //     numberOfColumns: '3',
      //     title: 'Directors List',
      //     data: this.getDirectorData(_res?.directors),
      //   },

      // ],
      organizationProfile: {
        title: _res?.org?.name,
        subtitle: _res?.org?.description,
        heroImage: {
          image:
            _res?.org?.logo ??
            `https://ui-avatars.com/api/?name=${_res.org?.name}&background=0D8ABC&color=fff`,
        },
        listItems: this.getOrgListItems(_res?.org),
        // buttons: featuresSection?.buttons,
        formDefaultData: {
          name: _res?.org?.name,
          description: _res?.org?.description,
          image: _res?.org?.logo,
          address: _res?.org?.address?.addressLine1,
          country: _res?.org?.address?.country,
          city: _res?.org?.address?.city,
          telephone: _res?.org?.primaryContactNo,
          website: _res?.org?.website,
        },
      },
    };
  };

  getProps(args?: any) {
    // const srv = getBreadcrumbService();
    // const breadcrumbItems = srv?.getBreadCrumbItems();
    console.log('find->', this.format(args));
    return {
      header: { ...header, ...{} },
      loading: loading,
      error: error,
      notifications: notifications,
      editProfile: editProfile,
      formData: formData,
      sampleData: this.format({
        lastName: 'Doe',
        firstName: 'John',
        middleName: 'Dae',
        role: 'Admin',
      }),
    };
  }
}
